import { useQuery } from '@tanstack/react-query';
import type { PublicOrganization } from '@dermalink/shared';
import { getConfig } from '../config.ts';
import { practiceThemeTokens } from '../utils/practiceTheme.ts';

/**
 * Unauthenticated practice info (name + branding) for a practice-scoped page (/praktijk/:slug) —
 * usable on the login page before the visitor has a session. Disabled without a slug.
 */
export function usePublicOrganization(slug: string | null | undefined) {
  return useQuery<PublicOrganization | null>({
    queryKey: ['public-organization', slug],
    enabled:  !!slug,
    queryFn:  async () => {
      const res = await fetch(`${getConfig().api_url}/public/organizations/${encodeURIComponent(slug!)}`);
      if (res.status === 404) return null;
      if (!res.ok) throw new Error('Failed to load practice');
      return res.json();
    },
  });
}

/** CSS custom property consumed by the photo-backdrop styles in AuthPageShell/PageLayout. */
function backdropStyle(url: string | null | undefined): React.CSSProperties | undefined {
  return url ? { '--_backdrop-image': `url("${url}")` } as React.CSSProperties : undefined;
}

/** The practice's branding as inline custom properties for a page wrapper: its backdrop and brand-colour tokens. */
export function practiceBrandingStyle(practice: PublicOrganization | null | undefined): React.CSSProperties | undefined {
  if (!practice) return undefined;
  return { ...backdropStyle(practice.backdrop_url), ...(practice.brand_color ? practiceThemeTokens(practice.brand_color) : {}) } as React.CSSProperties;
}
